import { VFXEffectConfig } from '../../VFXEffectRegistry';
import {
  ringBlast, shardBurst, glintScatter, groundCloud,
  starFlash, stagedBlast, wispRise,
} from './burstBuilders';
import { groundZone } from './zoneBuilders';

// =============================================================================
// MAGE EFFECTS
// =============================================================================

/**
 * Frost trail - cyan trail for frost bolt projectile
 */
const FROST_TRAIL: VFXEffectConfig = {
  id: 'frost_trail',
  name: 'Frost Trail',
  category: 'burst',
  description: 'Cyan trail for frost bolt projectile',
  primitives: [
    {
      type: 'circle',
      radius: 10,
      color: 0x00FFFF,
      alpha: 0.6,
      depth: 95,
      tween: {
        alpha: 0,
        scale: 1.5,
        duration: 300,
        ease: 'Power2'
      }
    },
    {
      type: 'star',
      points: 4,
      innerRadius: 3,
      outerRadius: 8,
      color: 0xFFFFFF,
      alpha: 0.8,
      depth: 96,
      tween: {
        rotation: Math.PI * 0.25,
        alpha: 0,
        duration: 400,
        ease: 'Power2'
      },
      scatter: { count: 3, radius: 12 }
    }
  ]
};

/**
 * Snow fall — glowing snow falling FROM ABOVE onto the blizzard zone
 * (VFX-evolution M2, theme H). DJ: the old version read as "random shards
 * exploding" because it scattered in a ring at the zone and fell only 100px.
 * Rebuild: spawn the snow well ABOVE the zone (offsetY) and drift it DOWN
 * with an accelerating ease + staggered layers, glow:true through the bloom
 * layer, over a soft frost ground glow. Colors are blizzard's authored
 * frost palette (#7ec8e3 / #a8d8ea / #d6eaf8 — abilityVfxColor('blizzard')).
 * Expectation: motion:'falling' (first appearance above the zone).
 */
const SNOW_FALL: VFXEffectConfig = {
  id: 'snow_fall',
  name: 'Snow Fall',
  category: 'burst',
  description: 'Glowing snow falling from above onto the blizzard zone',
  primitives: [
    // Soft frost ground glow at the zone — radial, expands + fades (bloom).
    {
      type: 'circle',
      radius: 60,
      color: 0x7ec8e3,
      alpha: 0.35,
      glow: true,
      tween: { scale: 1.3, alpha: 0, duration: 1200, ease: 'Sine.easeOut' },
    },
    // Falling snow layer A — spawns ABOVE the zone, accelerates DOWN.
    {
      type: 'circle',
      radius: 4,
      color: 0xd6eaf8,
      alpha: 0.9,
      glow: true,
      offsetY: -230,
      scatter: { count: 9, radius: 130 },
      tween: { y: 270, alpha: 0.15, duration: 1500, ease: 'Power2.easeIn' },
    },
    // Falling snow layer B — staggered, lighter, slower (rain-like depth).
    {
      type: 'circle',
      radius: 3,
      color: 0xa8d8ea,
      alpha: 0.8,
      glow: true,
      offsetY: -200,
      scatter: { count: 7, radius: 100 },
      tween: { y: 250, alpha: 0, duration: 1800, delay: 350, ease: 'Power2.easeIn' },
    },
  ],
};

/**
 * Mana shield bubble - blue protective barrier
 */
const MANA_SHIELD: VFXEffectConfig = {
  id: 'mana_shield',
  name: 'Mana Shield',
  category: 'aura',
  description: 'Blue magical protective barrier around the caster',
  followTarget: 'player',
  duration: 15000,
  primitives: [
    {
      type: 'ellipse',
      width: 75,
      height: 85,
      color: 0x4488FF,
      alpha: 0.15,
      depth: 50,
      stroke: { color: 0x66AAFF, width: 3 },
      tween: {
        rotation: Math.PI * 0.05,
        scale: 1.03,
        duration: 150,
        ease: 'Quad.easeInOut',
        yoyo: true,
        repeat: -1
      }
    },
    {
      type: 'circle',
      radius: 5,
      color: 0x4488FF,
      alpha: 0.6,
      depth: 51,
      tween: {
        alpha: 0,
        scale: 2,
        duration: 600,
        ease: 'Power2'
      },
      scatter: { count: 6, radius: 45 }
    }
  ]
};

/**
 * Arcane orbit - purple magic particles for arcane bolt muzzle
 */
const ARCANE_ORBIT: VFXEffectConfig = {
  id: 'arcane_orbit',
  name: 'Arcane Orbit',
  category: 'burst',
  description: 'Purple magic particles for arcane bolt muzzle',
  primitives: [
    {
      type: 'circle',
      radius: 15,
      color: 0xAA00FF,
      alpha: 0.7,
      depth: 95,
      tween: {
        alpha: 0,
        scale: 2,
        duration: 250,
        ease: 'Power2'
      }
    },
    {
      type: 'star',
      points: 4,
      innerRadius: 5,
      outerRadius: 12,
      color: 0xCC66FF,
      alpha: 0.9,
      depth: 96,
      tween: {
        rotation: Math.PI * 0.5,
        alpha: 0,
        duration: 300,
        ease: 'Power2'
      },
      scatter: { count: 4, radius: 20 }
    }
  ]
};

/**
 * Arcane Barrage Orbit - Floating arcane bolts that circle the caster
 */
const ARCANE_BARRAGE_ORBIT: VFXEffectConfig = {
  id: 'arcane_barrage_orbit',
  name: 'Arcane Barrage Orbit',
  category: 'orbit',
  description: 'Seven arcane bolts orbiting the caster',
  // F5-011: these orbs were authored 0x00FFFF/0x0000FF (FROST colors) — an
  // arcane signature that read as ice. Arcane family: violet core, deep
  // purple stroke (matches arcane_orbit + the authored #cc88ff projectile).
  primitives: Array.from({ length: 7 }, (_, i) => ({
    type: 'circle',
    radius: 10,
    color: 0xCC66FF,
    alpha: 0.8,
    depth: 100,
    stroke: { color: 0x6600AA, width: 3 },
    tween: {
      duration: 1000,
      repeat: -1,
      yoyo: true,
      alpha: 0.5
    },
    orbit: {
      radius: 50,
      speed: 1.5,
      startAngle: (i / 7) * Math.PI * 2,
      orientToPath: true
    }
  }))
};

/**
 * Meteor impact - massive fire explosion from the sky
 */
const METEOR_IMPACT: VFXEffectConfig = {
  id: 'meteor_impact',
  name: 'Meteor Impact',
  category: 'burst',
  description: 'Massive fire explosion for meteor ability',
  primitives: [
    { type: 'circle', radius: 60, color: 0xFF4500, alpha: 0.7, glow: true, stroke: { color: 0xFF6600, width: 3 },
      tween: { alpha: 0, scale: 2.5, duration: 500, ease: 'Power2' } },
    { type: 'circle', radius: 30, color: 0xFFFF00, alpha: 0.9, glow: true,
      tween: { alpha: 0, scale: 2, duration: 350, ease: 'Power2' } },
    { type: 'circle', radius: 12, color: 0xFFFFFF, alpha: 1, glow: true,
      tween: { alpha: 0, scale: 1.5, duration: 200, ease: 'Power1' } },
    { type: 'circle', radius: 5, color: 0xFF3300, alpha: 0.8, glow: true, depth: 95,
      tween: { alpha: 0, scale: 0.3, duration: 700, ease: 'Power2' }, scatter: { count: 16, radius: 80 } },
    { type: 'star', points: 4, innerRadius: 4, outerRadius: 10, color: 0xFFAA00, alpha: 0.9, glow: true,
      tween: { rotation: Math.PI * 0.5, alpha: 0, duration: 500, ease: 'Power2' }, scatter: { count: 8, radius: 60 } }
  ],
  screenShake: { intensity: 15, duration: 500 },
  screenFlash: { color: 0xFF4500, duration: 200 }
};

/**
 * Meteor streak — the meteor descending FROM ABOVE onto the zone during the
 * 600ms windup (VFX-evolution M2, theme H). Spawned at the zone by
 * AOEBehavior.windUpVfxId; offsetY puts it high and an accelerating fall
 * lands it as the impact fires. Glow through the bloom layer.
 */
const METEOR_STREAK: VFXEffectConfig = {
  id: 'meteor_streak',
  name: 'Meteor Streak',
  category: 'burst',
  description: 'Fiery meteor descending from the sky onto the impact zone',
  primitives: [
    // Meteor head — bright glowing ball falling from above.
    { type: 'circle', radius: 16, color: 0xff7a18, alpha: 0.95, glow: true,
      offsetY: -300, tween: { y: 300, scale: 1.3, duration: 600, ease: 'Power2.easeIn' } },
    // White-hot core.
    { type: 'circle', radius: 7, color: 0xffe08a, alpha: 1, glow: true,
      offsetY: -300, tween: { y: 300, duration: 600, ease: 'Power2.easeIn' } },
    // Trailing embers, also falling + fading.
    { type: 'circle', radius: 3, color: 0xff4500, alpha: 0.8, glow: true,
      offsetY: -300, scatter: { count: 5, radius: 18 },
      tween: { y: 280, alpha: 0, duration: 650, ease: 'Power2.easeIn' } },
  ],
};

/**
 * Meteor warning - matches meteor_streak descending onto the zone during windup
 */
const METEOR_WARNING: VFXEffectConfig = {
  ...METEOR_STREAK,
  id: 'meteor_warning',
  name: 'Meteor Warning',
};

/**
 * Meteor charge - swirling fire at caster during wind-up
 */
const METEOR_CHARGE: VFXEffectConfig = {
  id: 'meteor_charge',
  name: 'Meteor Charge',
  category: 'burst',
  description: 'Swirling fire during meteor wind-up',
  primitives: [
    { type: 'circle', radius: 20, color: 0xFF4500, alpha: 0.6, depth: 94,
      tween: { alpha: 0, scale: 2, duration: 400, ease: 'Power2' } },
    { type: 'circle', radius: 4, color: 0xFFFF00, alpha: 0.8, depth: 95,
      tween: { y: -30, alpha: 0, duration: 500, ease: 'Power2' }, scatter: { count: 6, radius: 20 } }
  ]
};

/**
 * Lightning strike - electric bolt flash
 */
const LIGHTNING_STRIKE: VFXEffectConfig = {
  id: 'lightning_strike',
  name: 'Lightning Strike',
  category: 'burst',
  description: 'Electric flash for lightning bolt',
  screenFlash: { color: 0xFFFF00, duration: 80 },
  primitives: [
    // Jagged beam bolt (uses glow-layered beam rendering)
    { type: 'beam', beamLength: 100, beamWidth: 4, beamSegments: 5, lineJitter: 12,
      color: 0xFFFF00, alpha: 0.9, depth: 94,
      tween: { alpha: 0, duration: 250, ease: 'Power1' } },
    // Horizontal flash rect
    { type: 'rect', width: 60, height: 3, color: 0xFFFF00, alpha: 0.9, depth: 95,
      tween: { alpha: 0, scaleX: 2, scaleY: 0.2, duration: 150, ease: 'Power1' } },
    // Bright core flash
    { type: 'circle', radius: 15, color: 0xFFFFFF, alpha: 0.8, depth: 96,
      tween: { alpha: 0, scale: 2, duration: 200, ease: 'Power1' } },
    // Electric sparks
    { type: 'star', points: 4, innerRadius: 3, outerRadius: 8, color: 0xFFFF88, alpha: 0.9, depth: 97,
      tween: { rotation: Math.PI * 0.25, alpha: 0, duration: 250, ease: 'Power2' }, scatter: { count: 4, radius: 20 } }
  ]
};

/**
 * Lightning charge - crackling energy during wind-up
 */
const LIGHTNING_CHARGE: VFXEffectConfig = {
  id: 'lightning_charge',
  name: 'Lightning Charge',
  category: 'burst',
  description: 'Crackling energy during lightning wind-up',
  primitives: [
    { type: 'circle', radius: 12, color: 0xFFFF00, alpha: 0.7, depth: 95,
      tween: { alpha: 0, scale: 1.8, duration: 200, ease: 'Power2' } },
    { type: 'star', points: 4, innerRadius: 2, outerRadius: 6, color: 0xFFFFFF, alpha: 0.8, depth: 96,
      tween: { rotation: Math.PI * 0.5, alpha: 0, duration: 200, ease: 'Power2' }, scatter: { count: 3, radius: 15 } }
  ]
};

/**
 * Ice wall - frost barrier zone
 */
const ICE_WALL: VFXEffectConfig = {
  id: 'ice_wall',
  name: 'Ice Wall',
  category: 'zone',
  description: 'Frost barrier zone for ice wall ability',
  // Lifetime ceiling (zone-builders spec §4, contract rule 3): every zone def
  // carries a top-level duration as the last-resort cap — matches
  // IceWallAbility's 5s summon. The repeat:-1 primitives below are legal ONLY
  // because this ceiling + the instanceId→playPersistent stop path bound
  // them. The original leak: registered def, repeat:-1, no duration, and
  // spawnEffectFromData ignored the summon's instanceId → walls persisted
  // until scene swap. PF-ZONE-NO-DURATION now guards the whole class.
  duration: 5000,
  primitives: [
    { type: 'rect', width: 20, height: 80, color: 0x88CCFF, alpha: 0.4, depth: 50, stroke: { color: 0xAADDFF, width: 2 },
      tween: { alpha: 0.2, scale: 1.03, duration: 500, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' } },
    { type: 'star', points: 4, innerRadius: 3, outerRadius: 8, color: 0xFFFFFF, alpha: 0.6, depth: 51,
      tween: { y: -20, alpha: 0, rotation: Math.PI * 0.25, duration: 800, repeat: -1 }, scatter: { count: 4, radius: 15 } }
  ]
};

/**
 * Ring of Frost — frost zone trap. Ground ellipse rim + orbiting ice stars.
 * Matches RingOfFrostAbility 15s summon duration and radius 80.
 */
const RING_OF_FROST: VFXEffectConfig = {
  id: 'ring_of_frost',
  name: 'Ring of Frost',
  category: 'zone',
  description: 'Expanding ice rim freezing enemies within its boundary',
  duration: 15000,
  primitives: [
    ...groundZone({
      ring: { width: 160, fill: 0x44AAEE, rim: 0x88DDFF, alpha: 0.18, rimWidth: 3, pulse: { alpha: 0.32, duration: 1400 } },
      edge: { count: 8, rimRadius: 75, shape: 'star', size: 6, color: 0xCCEEFF, alpha: 0.85, speed: 0.15, glow: true },
    }),
  ],
};

const FROST_RING: VFXEffectConfig = {
  ...RING_OF_FROST,
  id: 'frost_ring',
  name: 'Frost Ring',
};

/**
 * Fireball cast charge - swirling fire during wind-up
 */
const FIREBALL_CAST_CHARGE: VFXEffectConfig = {
  id: 'fireball_cast_charge',
  name: 'Fireball Charge',
  category: 'burst',
  description: 'Swirling fire during fireball wind-up',
  primitives: [
    // VFX-evolution M1.S5 demo — soft luminous core through the bloom layer
    // (glow:true → additive + the glow layer's blur = bloom). The visible
    // proof of the M1 substrate. Cookbook: VFX_BUILDER_COOKBOOK.
    {
      type: 'circle',
      radius: 22,
      color: 0xff7a18,
      alpha: 0.9,
      glow: true,
      tween: {
        scale: 1.7,
        alpha: 0,
        duration: 360,
        ease: 'Power2'
      }
    },
    {
      type: 'circle',
      radius: 15,
      color: 0xFF4500,
      alpha: 0.7,
      depth: 94,
      tween: {
        scale: 1.5,
        alpha: 0,
        duration: 300,
        ease: 'Power2'
      }
    },
    {
      type: 'star',
      points: 5,
      innerRadius: 4,
      outerRadius: 10,
      color: 0xFFAA33,
      alpha: 0.9,
      glow: true,
      scatter: { count: 4, radius: 20 },
      tween: {
        alpha: 0,
        scale: 0.5,
        rotation: Math.PI,
        duration: 400,
        ease: 'Power1'
      }
    }
  ]
};

/**
 * Arcane missiles burst - rapid purple bolt burst at muzzle
 */
const ARCANE_MISSILES_BURST: VFXEffectConfig = {
  id: 'arcane_missiles_burst',
  name: 'Arcane Missiles Burst',
  category: 'burst',
  description: 'Purple bolt burst for arcane missiles muzzle flash',
  primitives: [
    // Central arcane flash
    { type: 'circle', radius: 18, color: 0xAA00FF, alpha: 0.8, depth: 95,
      tween: { alpha: 0, scale: 2, duration: 200, ease: 'Power2' } },
    // Bolt streaks radiating out
    { type: 'rect', width: 20, height: 3, color: 0xCC66FF, alpha: 0.9, depth: 96,
      tween: { alpha: 0, scaleX: 2.5, duration: 180, ease: 'Power2' },
      scatter: { count: 3, radius: 25 } },
    // Sparkle tips
    { type: 'star', points: 4, innerRadius: 2, outerRadius: 6, color: 0xFFFFFF, alpha: 0.8, depth: 97,
      tween: { rotation: Math.PI * 0.25, alpha: 0, duration: 250, ease: 'Power2' },
      scatter: { count: 3, radius: 18 } }
  ]
};

/**
 * Casting glow - generic magical glow during ability wind-up
 */
const CASTING_GLOW: VFXEffectConfig = {
  id: 'casting_glow',
  name: 'Casting Glow',
  category: 'burst',
  description: 'Soft magical glow during generic ability casting',
  primitives: [
    // Outer soft glow
    { type: 'circle', radius: 25, color: 0x6688FF, alpha: 0.4, depth: 93,
      tween: { alpha: 0, scale: 1.8, duration: 500, ease: 'Power2' } },
    // Inner bright core
    { type: 'circle', radius: 10, color: 0xAABBFF, alpha: 0.7, depth: 95,
      tween: { alpha: 0, scale: 1.5, duration: 350, ease: 'Power2' } },
    // Rising mana sparkles
    { type: 'star', points: 4, innerRadius: 2, outerRadius: 5, color: 0xFFFFFF, alpha: 0.6, depth: 96,
      tween: { y: -15, alpha: 0, rotation: Math.PI * 0.25, duration: 400, ease: 'Power2' },
      scatter: { count: 3, radius: 15 } }
  ]
};

/**
 * Combustion — fiery cast burst for Combustion buff activation
 */
const COMBUSTION: VFXEffectConfig = {
  id: 'combustion',
  name: 'Combustion',
  category: 'burst',
  description: 'Fiery activation burst for Combustion buff',
  primitives: [
    {
      type: 'circle',
      radius: 30,
      color: 0xFF4400,
      alpha: 0.8,
      depth: 94,
      tween: { alpha: 0, scale: 2.5, duration: 400, ease: 'Power2' },
    },
    {
      type: 'circle',
      radius: 15,
      color: 0xFFAA00,
      alpha: 0.9,
      depth: 95,
      tween: { alpha: 0, scale: 1.8, duration: 300, ease: 'Power2' },
    },
    {
      type: 'star',
      points: 4,
      innerRadius: 4,
      outerRadius: 10,
      color: 0xFF6600,
      alpha: 0.9,
      depth: 96,
      tween: { rotation: Math.PI * 0.5, alpha: 0, duration: 350, ease: 'Power2' },
      scatter: { count: 6, radius: 25 },
    },
    {
      type: 'circle',
      radius: 5,
      color: 0xFFCC44,
      alpha: 1.0,
      depth: 97,
      tween: { y: -30, alpha: 0, duration: 500, ease: 'Power2' },
      scatter: { count: 8, radius: 20 },
    },
  ],
};

/**
 * Frost Shield — icy defensive barrier for Ice Barrier ability
 */
const FROST_SHIELD: VFXEffectConfig = {
  id: 'frost_shield',
  name: 'Frost Shield',
  category: 'aura',
  description: 'Frosty ice barrier rotating around the caster',
  followTarget: 'player',
  duration: 8000,
  primitives: [
    {
      type: 'ellipse',
      width: 70,
      height: 80,
      color: 0x88CCFF,
      alpha: 0.12,
      depth: 50,
      stroke: { color: 0xAADDFF, width: 3 },
      tween: {
        rotation: -Math.PI * 0.05,
        scale: 1.03,
        duration: 200,
        ease: 'Quad.easeInOut',
        yoyo: true,
        repeat: -1,
      },
    },
    {
      type: 'circle',
      radius: 4,
      color: 0xCCEEFF,
      alpha: 0.7,
      depth: 51,
      tween: { alpha: 0, scale: 1.5, duration: 800, ease: 'Power2' },
      scatter: { count: 5, radius: 40 },
    },
    {
      type: 'star',
      points: 6,
      innerRadius: 2,
      outerRadius: 5,
      color: 0xFFFFFF,
      alpha: 0.5,
      depth: 52,
      tween: {
        rotation: Math.PI * 0.15,
        alpha: 0,
        duration: 1200,
        ease: 'Power2',
      },
      scatter: { count: 3, radius: 35 },
    },
  ],
};

// =============================================================================
// K21 HEAT-MAP — bespoke impacts for the abilities that until now shared one
// generic flash (arcane_burst ×6, fire_explosion ×5, fire_burst ×4,
// frost_shatter ×3). Highest-signature spells get their own read; the shared
// CommonVFX defs stay as the floor for the low-signature rest.
// Palette doctrine: TOML-authored colors where declared (mage.toml
// projectile_color/aura_color), multi-tone ≥2 fills, one-shot ≤600ms.
// =============================================================================

/**
 * Arcane Missiles Impact — the STACCATO landing. Missiles are a stream, so
 * the impact is three violet pips popping in rapid succession (0/90/180ms)
 * with thin bolt slivers — machine-gun rhythm, nothing like the single
 * star-flash of the shared arcane_burst or arcane_blast's one heavy slug.
 */
const ARCANE_MISSILES_IMPACT: VFXEffectConfig = {
  id: 'arcane_missiles_impact',
  name: 'Arcane Missiles Impact',
  category: 'burst',
  description: 'Staccato triple violet pip landing for the missile stream',
  primitives: [
    ringBlast({ radius: 12, color: 0xCC66FF, alpha: 0.9, glow: true, depth: 96,
      expandTo: 1.7, durationMs: 160, ease: 'Power3' }),
    ringBlast({ radius: 12, color: 0xAA00FF, alpha: 0.85, glow: true, depth: 96,
      expandTo: 1.7, durationMs: 160, ease: 'Power3', delayMs: 90 }),
    ringBlast({ radius: 12, color: 0xCC66FF, alpha: 0.8, glow: true, depth: 96,
      expandTo: 1.7, durationMs: 160, ease: 'Power3', delayMs: 180 }),
    // Thin bolt slivers thrown off each pip.
    shardBurst({ count: 4, spread: 20, color: 0xE0B3FF, alpha: 0.9, depth: 95,
      shard: { width: 10, height: 2 }, travel: 46, durationMs: 240 }),
    glintScatter({ count: 4, spread: 22, color: 0xFFFFFF, radius: 2, durationMs: 180, delayMs: 120 }),
  ],
};

/**
 * Arcane Blast Impact — ONE heavy charged slug (authored #cc88ff projectile).
 * A brief implosion (the charge collapsing into the target) then a single
 * fat violet detonation ring under a sharp 6-star. One beat, real weight —
 * the deliberate opposite of the missiles' staccato.
 */
const ARCANE_BLAST_IMPACT: VFXEffectConfig = {
  id: 'arcane_blast_impact',
  name: 'Arcane Blast Impact',
  category: 'burst',
  description: 'Single heavy violet slug — implosion beat into one fat ring',
  primitives: [
    // Charge collapse — implodes INTO the hit point first.
    { type: 'circle', radius: 26, color: 0xAA00FF, alpha: 0.5, depth: 93,
      tween: { alpha: 0, scale: 0.3, duration: 200, ease: 'Power2' } },
    starFlash({ points: 6, innerRadius: 10, outerRadius: 30, color: 0xCC88FF,
      glow: true, depth: 96, spinRad: Math.PI / 3, expandTo: 1.5, durationMs: 300, delayMs: 90 }),
    ringBlast({ radius: 30, color: 0x7711CC, alpha: 0.7, depth: 94,
      stroke: { color: 0xCC88FF, width: 3 }, expandTo: 2.3, durationMs: 400, delayMs: 90 }),
    glintScatter({ count: 6, spread: 30, color: 0xFFFFFF, durationMs: 220, delayMs: 160 }),
  ],
};

/**
 * Arcane Detonation — the AoE arcane heavies (arcane_explosion /
 * arcane_surge / the barrage pair). Wide staged double ring (pale core →
 * deep-violet pressure wave) hurling crystalline slivers FAR — a room-clearer
 * read, distinct from both the point-impact defs above.
 */
const ARCANE_DETONATION: VFXEffectConfig = {
  id: 'arcane_detonation',
  name: 'Arcane Detonation',
  category: 'burst',
  description: 'Wide staged violet AoE ring hurling crystalline slivers',
  primitives: [
    ...stagedBlast(
      { radius: 24, color: 0xE0CCFF, alpha: 0.9, glow: true, depth: 96, expandTo: 2.0, durationMs: 240 },
      { radius: 44, color: 0xAA00FF, alpha: 0.7, depth: 94,
        stroke: { color: 0x6600AA, width: 4 }, expandTo: 2.8, durationMs: 480 },
      100,
    ),
    shardBurst({ count: 9, spread: 60, color: 0xCC66FF, glow: true, depth: 95,
      shard: { points: [0, -12, 4, 5, -4, 5] }, travel: 120, durationMs: 420 }),
    starFlash({ points: 6, innerRadius: 6, outerRadius: 16, color: 0xFFFFFF, alpha: 0.8, depth: 97, durationMs: 260 }),
  ],
};

/**
 * Pyroblast Impact — the STAGED HEAVY. A comet-slam: white-hot contact
 * flash, then TWO delayed pressure waves (orange → deep red afterwave),
 * flame tongues, and a rising fire plume over a dark scorch. Triple-staged
 * where fire_explosion blooms once — this is the biggest single-target hit
 * in the fire book and it should feel sequenced, not instant.
 */
const PYROBLAST_IMPACT: VFXEffectConfig = {
  id: 'pyroblast_impact',
  name: 'Pyroblast Impact',
  category: 'burst',
  description: 'Triple-staged comet slam — flash, double pressure wave, plume',
  primitives: [
    // Stage 1 — white-hot contact flash.
    ringBlast({ radius: 28, color: 0xFFF4C2, alpha: 0.95, glow: true, depth: 97,
      expandTo: 1.8, durationMs: 200, ease: 'Power3' }),
    // Stage 2 — orange pressure wave.
    ringBlast({ radius: 44, color: 0xFF5500, alpha: 0.85, glow: true, depth: 94,
      stroke: { color: 0xFFAA33, width: 4 }, expandTo: 2.8, durationMs: 480, delayMs: 110 }),
    // Stage 3 — deep-red afterwave, the lingering heat front.
    ringBlast({ radius: 54, color: 0xCC2200, alpha: 0.55, depth: 93,
      expandTo: 3.0, durationMs: 520, delayMs: 230 }),
    // Flame tongues hurled from the crater.
    shardBurst({ count: 12, spread: 70, color: 0xFF7722, alpha: 0.9, glow: true, depth: 95,
      shard: { points: [0, -16, 5, 6, 0, 12, -5, 6] }, travel: 160, durationMs: 460 }),
    // Rising fire plume — big embers climbing out of the slam.
    wispRise({ count: 8, spread: 26, radius: 5, color: 0xFFB347, glow: true,
      travel: 70, durationMs: 600, delayMs: 150, depth: 96 }),
    groundCloud({ width: 110, height: 28, color: 0x551A00, alpha: 0.5, depth: 92,
      offsetY: 20, swellTo: 1.4, durationMs: 600 }),
  ],
  screenShake: { intensity: 13, duration: 420 },
  screenFlash: { color: 0xFF5500, duration: 140 },
};

/**
 * Fireball Impact — the signature spammable's own bloom. Classic warm
 * double ring + white-hot 4-star + a small smoke puff and short embers.
 * Smaller/softer than pyroblast, rounder than fire_burst's snap-pop (no
 * pinpoint core; it leaves smoke — a thrown ball of fire, not a jet).
 */
const FIREBALL_IMPACT: VFXEffectConfig = {
  id: 'fireball_impact',
  name: 'Fireball Impact',
  category: 'burst',
  description: 'Classic warm bloom with a white-hot star and a smoke puff',
  primitives: [
    ringBlast({ radius: 16, color: 0xFF6A00, alpha: 0.9, glow: true, depth: 95,
      expandTo: 2.0, durationMs: 240 }),
    ringBlast({ radius: 10, color: 0xFFD080, alpha: 0.85, glow: true, depth: 96,
      expandTo: 1.8, durationMs: 180, ease: 'Power3' }),
    starFlash({ points: 4, innerRadius: 4, outerRadius: 12, color: 0xFFF6DD,
      glow: true, depth: 97, durationMs: 200 }),
    wispRise({ count: 5, spread: 16, radius: 2.5, color: 0xFFAA33, glow: true,
      travel: 30, durationMs: 300, depth: 96 }),
    // The smoke puff fireball leaves behind — fire_burst never smokes.
    groundCloud({ width: 48, height: 14, color: 0x443322, alpha: 0.35, depth: 92,
      offsetY: 12, swellTo: 1.5, durationMs: 420 }),
  ],
};

/**
 * Ice Lance Impact — a shard-PIERCE, not a shatter. A thin horizontal
 * pierce streak snaps through the target, a TIGHT needle spray travels on
 * past it, one crisp small ring. Directional and needly (#88ccff lance) —
 * frost_shatter's radial brittle burst and react_shatter's shell-explosion
 * stay their own reads.
 */
const ICE_LANCE_IMPACT: VFXEffectConfig = {
  id: 'ice_lance_impact',
  name: 'Ice Lance Impact',
  category: 'burst',
  description: 'Thin pierce streak with a tight needle spray — through, not apart',
  primitives: [
    // The pierce line — a streak that stretches THROUGH the hit point.
    { type: 'rect', width: 46, height: 3, color: 0xE8F8FF, alpha: 0.95, depth: 97, glow: true,
      tween: { alpha: 0, scaleX: 2.2, scaleY: 0.5, duration: 200, ease: 'Power3' } },
    // Tight needle spray — small spread, long travel (momentum carries on).
    shardBurst({ count: 5, spread: 12, color: 0x88CCFF, alpha: 0.95, depth: 95,
      shard: { points: [0, -10, 2, 4, -2, 4] },
      stroke: { color: 0xFFFFFF, width: 1 }, travel: 70, durationMs: 300 }),
    ringBlast({ radius: 10, color: 0xCCF0FF, alpha: 0.5, depth: 94,
      stroke: { color: 0xFFFFFF, width: 1 }, expandTo: 1.8, durationMs: 200 }),
    glintScatter({ count: 3, spread: 16, color: 0xFFFFFF, radius: 2, durationMs: 160, delayMs: 60 }),
  ],
};

/**
 * Glacial Spike Impact — the HEAVY shatter. The frozen spike detonates:
 * big white-stroked spike shards, a delayed inner chip layer, staged pale
 * double ring, and a frost pool left on the ground. Everything frost_shatter
 * does, scaled up and sequenced — the capstone frost hit.
 */
const GLACIAL_SPIKE_IMPACT: VFXEffectConfig = {
  id: 'glacial_spike_impact',
  name: 'Glacial Spike Impact',
  category: 'burst',
  description: 'Heavy staged ice detonation — big spikes, chip layer, frost pool',
  primitives: [
    ...stagedBlast(
      { radius: 20, color: 0xE8F8FF, alpha: 0.8, glow: true, depth: 96, expandTo: 1.9, durationMs: 220 },
      { radius: 36, color: 0x99DDFF, alpha: 0.6, depth: 94,
        stroke: { color: 0xFFFFFF, width: 2 }, expandTo: 2.6, durationMs: 460 },
      100,
    ),
    // Big spike shards — the spike itself coming apart.
    shardBurst({ count: 8, spread: 34, color: 0x77BBEE, alpha: 0.95, glow: true, depth: 95,
      shard: { points: [0, -18, 5, 6, -5, 6] },
      stroke: { color: 0xFFFFFF, width: 2 }, travel: 90, durationMs: 420 }),
    // Delayed inner chip layer — the fine debris beat.
    shardBurst({ count: 6, spread: 20, color: 0xCCF0FF, alpha: 0.9, depth: 96,
      shard: { points: [0, -8, 3, 3, -3, 3] },
      stroke: { color: 0xFFFFFF, width: 1 }, durationMs: 300, delayMs: 60 }),
    groundCloud({ width: 80, height: 20, color: 0x6699CC, alpha: 0.4, depth: 92,
      offsetY: 16, stroke: { color: 0xCCF0FF, width: 1 }, swellTo: 1.4, durationMs: 560 }),
  ],
  screenShake: { intensity: 8, duration: 260 },
};

// =============================================================================
// EXPORTS
// =============================================================================


/**
 * Arcane Blink (shimmer) — blink-while-casting flicker. SPEC: the caster
 * "skips" — a violet ring collapses INWARD at the origin while two offset
 * after-images flank and fade; arcane sparkles scatter. Fast (≤350ms),
 * reads as displacement, not an attack.
 */
const ARCANE_BLINK: VFXEffectConfig = {
  id: 'arcane_blink',
  name: 'Arcane Blink',
  category: 'burst',
  description: 'Shimmer blink flicker — collapsing ring + after-images',
  primitives: [
    { type: 'particles', particleEffectId: 'arcane_burst', particleCount: 8 },
    // Collapsing ring — implodes to the blink point.
    { type: 'circle', radius: 34, color: 0xAA00FF, alpha: 0.6, depth: 95,
      tween: { alpha: 0, scale: 0.2, duration: 300, ease: 'Power2' } },
    // After-images: two flanking echoes that fade in place.
    { type: 'circle', radius: 14, color: 0xCC66FF, alpha: 0.55, depth: 94,
      tween: { alpha: 0, scale: 0.8, duration: 340, ease: 'Power1' },
      scatter: { count: 2, radius: 26 } },
    // Bright displacement spark at the origin.
    { type: 'star', points: 4, innerRadius: 4, outerRadius: 12, color: 0xFFFFFF, alpha: 0.9, depth: 96,
      tween: { rotation: Math.PI * 0.5, alpha: 0, scale: 0.3, duration: 250, ease: 'Power2' } },
  ],
};

/**
 * Fire Explosion — the big-fire-nuke impact (pyroblast, phoenix_flames, the
 * wildfire family — 5 abilities that until now ALIASED meteor_impact).
 *
 * Read: a blooming DETONATION at the point of impact, not meteor's
 * falling-crater. Staged double ring — white-hot flash first, then a
 * delayed orange-red pressure wave — with flame-tongue polygon slivers
 * thrown outward, warm embers drifting UP, and a brief dark ground scorch.
 * FX-grammar lane 4 (docs/plans/2026-07-17-fx-grammar-buildout.md):
 * composed from burstBuilders verbs; the two builder-derived tweaks
 * (delay on the outer ring, upward drift on the embers) are the flavor.
 */
const FIRE_EXPLOSION: VFXEffectConfig = {
  id: 'fire_explosion',
  name: 'Fire Explosion',
  category: 'burst',
  description: 'Blooming staged detonation for the big fire nukes',
  primitives: [
    // Stage 1 — white-hot core flash (instant, tight, blinding).
    ringBlast({
      radius: 26, color: 0xFFF0AA, alpha: 0.95, glow: true, depth: 96,
      expandTo: 1.9, durationMs: 220, ease: 'Power2',
    }),
    // Stage 2 — the orange-red pressure wave, a beat later (the "bloom").
    // Builder-derived: ringBlast + a short delay so the detonation STAGES.
    (() => {
      const wave = ringBlast({
        radius: 46, color: 0xFF4500, alpha: 0.8, glow: true, depth: 94,
        stroke: { color: 0xFF7733, width: 4 },
        expandTo: 2.9, durationMs: 520, ease: 'Power2',
      });
      wave.tween = { ...wave.tween!, delay: 110 };
      return wave;
    })(),
    // Flame tongues — polygon slivers hurled outward from the blast point.
    shardBurst({
      count: 10, spread: 72, color: 0xFF6622, alpha: 0.9, glow: true, depth: 95,
      shard: { points: [0, -16, 5, 6, 0, 12, -5, 6] },
      travel: 150, durationMs: 430,
    }),
    // Rising embers — warm glints that DRIFT UP as the blast settles.
    // Builder-derived: glintScatter + an upward y drift (fire rises).
    (() => {
      const embers = glintScatter({
        count: 9, spread: 44, color: 0xFFB347, radius: 3.5,
        durationMs: 640, delayMs: 140, depth: 97,
      });
      embers.tween = { ...embers.tween!, y: -58, ease: 'Sine.easeOut' };
      return embers;
    })(),
    // Ground scorch — brief dark pool under the detonation, quick fade.
    groundCloud({
      width: 100, height: 26, color: 0x662200, alpha: 0.5, depth: 93,
      offsetY: 20, swellTo: 1.35, durationMs: 560,
    }),
  ],
  screenShake: { intensity: 10, duration: 350 },
};

export const MAGE_VFX: VFXEffectConfig[] = [
  // K21 heat-map bespoke impacts
  ARCANE_MISSILES_IMPACT,
  ARCANE_BLAST_IMPACT,
  ARCANE_DETONATION,
  PYROBLAST_IMPACT,
  FIREBALL_IMPACT,
  ICE_LANCE_IMPACT,
  GLACIAL_SPIKE_IMPACT,
  FIRE_EXPLOSION,
  ARCANE_BLINK,
  FROST_TRAIL,
  SNOW_FALL,
  MANA_SHIELD,
  ARCANE_ORBIT,
  ARCANE_BARRAGE_ORBIT,
  METEOR_IMPACT,
  METEOR_STREAK,
  METEOR_WARNING,
  METEOR_CHARGE,
  LIGHTNING_STRIKE,
  LIGHTNING_CHARGE,
  ICE_WALL,
  RING_OF_FROST,
  FROST_RING,
  FIREBALL_CAST_CHARGE,
  ARCANE_MISSILES_BURST,
  CASTING_GLOW,
  COMBUSTION,
  FROST_SHIELD,
];
